import type {
    CheckboxFieldConfig,
    CheckboxGroupFieldConfig,
    CustomValidator,
    FieldCondition,
    FieldValidator,
    FormDataMap,
    FormFieldConfig,
    FormFieldValue,
    FormValues,
    RadioFieldConfig,
    SelectFieldConfig,
    TextAreaFieldConfig,
    TextFieldConfig,
} from '../../types/form-builder.types';
import { resolveCondition } from '../../utils/resolve-condition.utils';
import { isEmpty } from './field.utils';

interface RequiredValidatorMetadata {
    readonly type: 'required';
    readonly message: string;
    readonly condition?: boolean | FieldCondition;
}

interface CustomValidatorMetadata {
    readonly type: 'custom';
    readonly debounce?: number;
}

type ValidatorMetadata = RequiredValidatorMetadata | { readonly type: 'pattern' } | CustomValidatorMetadata;

type CustomValidationResult =
    | { readonly type: 'sync'; readonly error: string | undefined }
    | { readonly type: 'async'; readonly validation: Promise<string | undefined> };

interface TypedCustomValidationContext<
    Value extends FormFieldValue,
    FormValuesType extends FormValues,
    FieldId extends string,
> {
    readonly value: Value;
    readonly formValues: FormValuesType;
    readonly validators: ReadonlyArray<CustomValidator<Value, FormValuesType, FieldId>>;
}

interface FieldValueHandlers<Result> {
    readonly boolean: (value: boolean, fieldConfig: CheckboxFieldConfig) => Result;
    readonly array: (value: Array<string | number>, fieldConfig: CheckboxGroupFieldConfig) => Result;
    readonly stringOrNumber: (value: string | number, fieldConfig: RadioFieldConfig | SelectFieldConfig) => Result;
    readonly string: (value: string, fieldConfig: TextAreaFieldConfig | TextFieldConfig) => Result;
}

const findRequiredValidator = (
    validators: readonly ValidatorMetadata[] | undefined
): RequiredValidatorMetadata | undefined =>
    validators?.find((validator): validator is RequiredValidatorMetadata => validator.type === 'required');

const hasThen = (value: unknown): value is { readonly then: unknown } =>
    typeof value === 'object' && value !== null && 'then' in value;

const isPromiseLike = (value: unknown): value is PromiseLike<string | undefined> =>
    hasThen(value) && typeof value.then === 'function';

export const isFieldRequired = (fieldConfig: FormFieldConfig, formValues: FormValues, data?: FormDataMap): boolean => {
    const requiredValidator = findRequiredValidator(fieldConfig.validators);
    if (!requiredValidator) {
        return false;
    }
    return resolveCondition(requiredValidator.condition ?? true, formValues, data);
};

const getCustomValidators = <Value extends FormFieldValue, FormValuesType extends FormValues, FieldId extends string>(
    validators: ReadonlyArray<FieldValidator<Value, FormValuesType, FieldId>> | undefined
): Array<CustomValidator<Value, FormValuesType, FieldId>> =>
    (validators ?? []).filter(
        (validator): validator is CustomValidator<Value, FormValuesType, FieldId> => validator.type === 'custom'
    );

export const hasCustomValidators = (fieldConfig: FormFieldConfig): boolean =>
    fieldConfig.validators?.some((validator) => validator.type === 'custom') ?? false;

export const getMaxDebounceTime = (fieldConfig: FormFieldConfig): number =>
    (fieldConfig.validators ?? []).reduce((maxDelay, validator) => {
        if (validator.type !== 'custom') {
            return maxDelay;
        }
        return Math.max(maxDelay, validator.debounce ?? 0);
    }, 0);

export const createValidationRunner = (getData: () => FormDataMap | undefined) => {
    const runForFieldValue = <Result>(
        value: FormFieldValue,
        fieldConfig: FormFieldConfig,
        handlers: FieldValueHandlers<Result>
    ): Result | undefined => {
        switch (fieldConfig.type) {
            case 'checkbox': {
                return typeof value === 'boolean' ? handlers.boolean(value, fieldConfig) : undefined;
            }

            case 'checkbox-group': {
                return Array.isArray(value) ? handlers.array(value, fieldConfig) : undefined;
            }

            case 'radio':
            case 'select': {
                return typeof value === 'string' || typeof value === 'number'
                    ? handlers.stringOrNumber(value, fieldConfig)
                    : undefined;
            }

            case 'text':
            case 'number':
            case 'email':
            case 'textarea': {
                return typeof value === 'string' ? handlers.string(value, fieldConfig) : undefined;
            }
        }
    };

    const shouldSkipOptionalEmptyValidation = (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): boolean => !isFieldRequired(fieldConfig, formValues, getData()) && isEmpty(value);

    const shouldRunCustomValidation = (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): boolean =>
        hasCustomValidators(fieldConfig) && !shouldSkipOptionalEmptyValidation(value, formValues, fieldConfig);

    const runTypedSyncValidator = <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        validator: FieldValidator<Value, FormValuesType, FieldId>,
        value: Value,
        formValues: FormValuesType
    ): string | undefined => {
        switch (validator.type) {
            case 'required': {
                const isRequired = resolveCondition(validator.condition ?? true, formValues, getData());
                if (!isRequired) {
                    return;
                }
                return isEmpty(value) ? validator.message : undefined;
            }

            case 'pattern': {
                if (typeof value !== 'string') {
                    return undefined;
                }
                return validator.pattern.test(value) ? undefined : validator.message;
            }

            case 'custom': {
                return undefined;
            }
        }
    };

    const runTypedSyncValidators = <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        value: Value,
        formValues: FormValuesType,
        validators: ReadonlyArray<FieldValidator<Value, FormValuesType, FieldId>> | undefined
    ): string | undefined => {
        if (!validators) {
            return undefined;
        }

        for (const validator of validators) {
            const error = runTypedSyncValidator(validator, value, formValues);
            if (error !== undefined) {
                return error;
            }
        }

        return undefined;
    };

    const continueTypedCustomValidation = <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        context: TypedCustomValidationContext<Value, FormValuesType, FieldId>,
        startIndex: number,
        firstValidation: PromiseLike<string | undefined>
    ): Promise<string | undefined> =>
        Promise.resolve(firstValidation).then((firstError) => {
            if (firstError !== undefined) {
                return firstError;
            }

            return context.validators.slice(startIndex).reduce<Promise<string | undefined>>(
                (validation, validator) =>
                    validation.then(async (previousError) => {
                        if (previousError !== undefined) {
                            return previousError;
                        }
                        return validator.validate(context.value, context.formValues);
                    }),
                Promise.resolve<string | undefined>(void 0)
            );
        });

    const startTypedCustomValidation = <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        value: Value,
        formValues: FormValuesType,
        validators: ReadonlyArray<CustomValidator<Value, FormValuesType, FieldId>> | undefined
    ): CustomValidationResult => {
        if (!validators) {
            return { type: 'sync', error: undefined };
        }

        const context: TypedCustomValidationContext<Value, FormValuesType, FieldId> = { value, formValues, validators };

        for (const [index, validator] of validators.entries()) {
            const result = validator.validate(value, formValues);
            if (isPromiseLike(result)) {
                return {
                    type: 'async',
                    validation: continueTypedCustomValidation(context, index + 1, result),
                };
            }
            if (result !== undefined) {
                return { type: 'sync', error: result };
            }
        }

        return { type: 'sync', error: undefined };
    };

    const runTypedCustomValidation = async <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        value: Value,
        formValues: FormValuesType,
        validators: ReadonlyArray<CustomValidator<Value, FormValuesType, FieldId>> | undefined
    ): Promise<string | undefined> => {
        const result = startTypedCustomValidation(value, formValues, validators);
        return result.type === 'sync' ? result.error : result.validation;
    };

    const runTypedFullValidation = <
        Value extends FormFieldValue,
        FormValuesType extends FormValues,
        FieldId extends string,
    >(
        value: Value,
        formValues: FormValuesType,
        validators: ReadonlyArray<FieldValidator<Value, FormValuesType, FieldId>> | undefined
    ): Promise<string | undefined> => {
        if (!validators) {
            return Promise.resolve<string | undefined>(void 0);
        }

        return validators.reduce<Promise<string | undefined>>(
            (validation, validator) =>
                validation.then(async (previousError) => {
                    if (previousError !== undefined) {
                        return previousError;
                    }
                    if (validator.type !== 'custom') {
                        return runTypedSyncValidator(validator, value, formValues);
                    }
                    return validator.validate(value, formValues);
                }),
            Promise.resolve<string | undefined>(void 0)
        );
    };

    const runSynchronousValidation = (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): string | undefined => {
        if (shouldSkipOptionalEmptyValidation(value, formValues, fieldConfig)) {
            return undefined;
        }

        return runForFieldValue(value, fieldConfig, {
            array: (arrayValue, config) => runTypedSyncValidators(arrayValue, formValues, config.validators),
            boolean: (booleanValue, config) => runTypedSyncValidators(booleanValue, formValues, config.validators),
            string: (stringValue, config) => runTypedSyncValidators(stringValue, formValues, config.validators),
            stringOrNumber: (stringOrNumberValue, config) =>
                runTypedSyncValidators(stringOrNumberValue, formValues, config.validators),
        });
    };

    const runCustomValidationOnly = async (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): Promise<string | undefined> => {
        if (shouldSkipOptionalEmptyValidation(value, formValues, fieldConfig)) {
            return undefined;
        }

        return runForFieldValue(value, fieldConfig, {
            array: (arrayValue, config) =>
                runTypedCustomValidation(arrayValue, formValues, getCustomValidators(config.validators)),
            boolean: (booleanValue, config) =>
                runTypedCustomValidation(booleanValue, formValues, getCustomValidators(config.validators)),
            string: (stringValue, config) =>
                runTypedCustomValidation(stringValue, formValues, getCustomValidators(config.validators)),
            stringOrNumber: (stringOrNumberValue, config) =>
                runTypedCustomValidation(stringOrNumberValue, formValues, getCustomValidators(config.validators)),
        });
    };

    const getEmptyCustomValidationResult = (): CustomValidationResult => ({ type: 'sync', error: undefined });

    const startCustomValidationOnly = (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): CustomValidationResult => {
        if (shouldSkipOptionalEmptyValidation(value, formValues, fieldConfig)) {
            return getEmptyCustomValidationResult();
        }

        return (
            runForFieldValue(value, fieldConfig, {
                array: (arrayValue, config) =>
                    startTypedCustomValidation(arrayValue, formValues, getCustomValidators(config.validators)),
                boolean: (booleanValue, config) =>
                    startTypedCustomValidation(booleanValue, formValues, getCustomValidators(config.validators)),
                string: (stringValue, config) =>
                    startTypedCustomValidation(stringValue, formValues, getCustomValidators(config.validators)),
                stringOrNumber: (stringOrNumberValue, config) =>
                    startTypedCustomValidation(stringOrNumberValue, formValues, getCustomValidators(config.validators)),
            }) ?? getEmptyCustomValidationResult()
        );
    };

    const runFullValidation = async (
        value: FormFieldValue,
        formValues: FormValues,
        fieldConfig: FormFieldConfig
    ): Promise<string | undefined> => {
        if (shouldSkipOptionalEmptyValidation(value, formValues, fieldConfig)) {
            return undefined;
        }

        return runForFieldValue(value, fieldConfig, {
            array: (arrayValue, config) => runTypedFullValidation(arrayValue, formValues, config.validators),
            boolean: (booleanValue, config) => runTypedFullValidation(booleanValue, formValues, config.validators),
            string: (stringValue, config) => runTypedFullValidation(stringValue, formValues, config.validators),
            stringOrNumber: (stringOrNumberValue, config) =>
                runTypedFullValidation(stringOrNumberValue, formValues, config.validators),
        });
    };

    return {
        runCustomValidationOnly,
        runSynchronousValidation,
        runFullValidation,
        shouldRunCustomValidation,
        startCustomValidationOnly,
    };
};
